import { useCallback, useRef, useState } from 'react'

import { useMetadata } from '@scoir/tracking'
import { isValid, parse } from 'date-fns'
import { validate } from 'email-validator'
import { useDispatch, useSelector } from 'react-redux'

import { getHeaders } from 'common/event-data-helper'
import metadataToTrackedEvent from 'common/metadata-to-tracked-event'
import { DOMAIN_TYPES, EVENT_TYPES } from 'common/track-event-constants'
import trackEvent from 'common/track-events'
import CollegeSwitchSurvey from 'components/CollegeSwitchSurvey'
import { isPhoneValid } from 'components/PhoneForm/validation'
import { useIsStudentInterestShareEnabled } from 'store/feature-toggle/hooks'
import prospectActions from 'store/prospects/actions'
import { CollegeDetail } from 'store/prospects/type-definitions'
import { actions as snactions } from 'store/snackbar'
import { selectUser } from 'store/user'
import userActions from 'store/user/actions'

import StudentShareInterestModal from '../StudentShareInterestModal'
import ShareInterestContext, { DOBError, ErrorDetails, StudentInfo } from './Context'
import { saveCollegeInterest, updateStudentInterest } from './share-interest-service'

type ValidatableFields = 'firstName' | 'lastName' | 'email' | 'dateOfBirth' | 'phoneNumber'

const validatableFields: ValidatableFields[] = ['firstName', 'lastName', 'email', 'dateOfBirth', 'phoneNumber']
function isValidatableField(field: keyof StudentInfo): field is ValidatableFields {
  return validatableFields.includes(field as ValidatableFields)
}

const ShareInterestProvider = ({ children }: { children: React.ReactNode }) => {
  const studentShareInterestEnabled = useIsStudentInterestShareEnabled()
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const user = useSelector(selectUser)
  const [onFollow, setOnFollow] = useState(false)
  const [selectedCollege, setSelectedCollege] = useState<CollegeDetail | null>(null)
  const [referrer, setReferrer] = useState<string>('my-colleges')
  // This is used for opening the follow survey
  const followSurveyRef = useRef<null | (() => void)>(null)
  const [showSurvey, setShowSurvey] = useState(false)

  const [preferenceType, setPreferenceType] = useState<'share_with_all_colleges' | 'ask_me_every_time'>(
    user.StudentInterestSharingPreference?.preferenceType ?? 'share_with_all_colleges',
  )
  const firstTime = user.StudentInterestSharingPreference?.preferenceType === undefined
  const prePopulate = user.HighSchool?.PrepopulateStudentInfo && firstTime
  const ots = !user.HighSchool && firstTime

  // Track Event Data
  const [get] = useMetadata()
  const data = metadataToTrackedEvent(get(), EVENT_TYPES.INTEREST_SHARING, { flat: true })
  const getOutcome = () => {
    if (preferenceType === 'ask_me_every_time') {
      return selectedCollege ? 'Share Info Specific College' : 'Ask Each Time'
    } else {
      return 'Share Info All College'
    }
  }
  const metaData = {
    ...data,
    DomainType: DOMAIN_TYPES.INTEREST_SHARING,
    EventSourceReferrer: referrer,
    EventData: {
      scid: selectedCollege ? selectedCollege.Scid : undefined,
      CurrentUrl: window.location.pathname,
      OptInOutcome: getOutcome(),
      // "On Follow" - When sharing from a follow event
      // "Post Follow" - When sharing after following an event (MCDT, Messages, CDP, etc)
      // "Global" - MCDT global share interest
      OptInSource: onFollow ? 'On Follow' : selectedCollege ? 'Post Follow' : 'Global',
    },
  }
  const options = getHeaders({ ...metaData, ...metaData.EventData })

  const [errors, setErrors] = useState<ErrorDetails>({} as ErrorDetails)
  const existingErrors = useCallback(() => {
    return Object.values(errors).some((value) => (typeof value === 'boolean' ? value : value.hasError))
  }, [errors])

  const getStudentInfo = () => {
    return {
      ...user.StudentInterestSharingPreference?.studentInfo,
      // Only populate academicFocuses if first time seeing modal
      ...(firstTime
        ? {
            academicFocuses: user.MustHave?.AcademicFocus,
          }
        : {}),
      // Only pre-populate fields if prePopulate is true or off the street student (first time seeing modal)
      ...(prePopulate || ots
        ? {
            firstName: user.FirstName || '',
            lastName: user.LastName || '',
            dateOfBirth: user.Profile?.BirthDate || '',
          }
        : {}),
    }
  }
  const [student, setStudent] = useState<StudentInfo>(getStudentInfo())

  const requiredFieldsIncomplete =
    !student.firstName || !student.lastName || !student.email || !student.dateOfBirth || existingErrors()

  const updateDetails = (fieldName: keyof StudentInfo, value: StudentInfo[keyof StudentInfo]) => {
    if (isValidatableField(fieldName)) {
      const hasError = validateDetail(fieldName, value)
      setErrors((prev) => ({
        ...prev,
        [fieldName]: fieldName === 'dateOfBirth' ? (hasError as DOBError) : (hasError as boolean),
      }))
    }
    setStudent((prevState) => ({ ...prevState, [fieldName]: value }))
  }

  const validateDetail = (fieldName: ValidatableFields, value: StudentInfo[keyof StudentInfo]): boolean | DOBError => {
    switch (fieldName) {
      case 'firstName':
      case 'lastName':
        return value === ''
      case 'email':
        return !validate(value as string)
      case 'dateOfBirth': {
        const date = parse(value as string, 'yyyy-MM-dd', new Date())

        if (!isValid(date)) {
          return { hasError: true, reason: 'invalid-date' }
        }

        if (calculateAge(date) < 13) {
          return { hasError: true, reason: 'under-13' }
        }

        return { hasError: false }
      }
      case 'phoneNumber':
        return !isPhoneValid(value)
    }
  }

  const handleOpen = () => {
    // Opening the modal without a college selection
    setSelectedCollege(null)
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
    setIsSaving(false)
    // Show the follow survey on follow no matter the action
    if (onFollow) {
      setShowSurvey(true)
    }
  }
  const handleDismiss = () => {
    if (onFollow) {
      trackEvent({
        ...metaData,
        EventData: {
          ...metaData.EventData,
          OptInOutcome: 'Not Now',
        },
      })
    }
    setErrors({} as ErrorDetails)
    handleClose()
  }
  const handleAfterClose = () => {
    setStudent(getStudentInfo())
    setPreferenceType(user.StudentInterestSharingPreference?.preferenceType ?? 'share_with_all_colleges')
    if (!onFollow) {
      // Reset college selection on exit
      setSelectedCollege(null)
    }
  }
  const selectCollege = (college: CollegeDetail | null, referrer?: string, callback?: () => void) => {
    if (callback) followSurveyRef.current = callback
    setSelectedCollege(college)
    setOnFollow(Boolean(referrer))
    setReferrer(referrer || 'my-colleges')
    setOpen(true)
  }
  const handleSave = async () => {
    setIsSaving(true)
    // Ignore save if nothing has changed
    if (
      preferenceType === user.StudentInterestSharingPreference?.preferenceType &&
      shallowEqual(student, user.StudentInterestSharingPreference?.studentInfo || {}) &&
      !selectedCollege
    ) {
      handleClose()
      return
    }
    // Save
    try {
      if (selectedCollege && preferenceType !== 'share_with_all_colleges') {
        await saveCollegeInterest({ scid: selectedCollege.Scid, studentInfo: student }, options)
      } else {
        await updateStudentInterest({ studentInfo: student, preferenceType }, options)
      }
      // success - re-fetch user and prospects
      await Promise.all([
        dispatch(userActions.fetchUser()),
        // If sharing with all colleges
        // we need to locally update prospect.StudentSharedInterest to true
        // to avoid race conditions
        dispatch(prospectActions.fetchProspects({ sharedInterest: preferenceType === 'share_with_all_colleges' })),
      ])
    } catch (error) {
      // failure
      await Promise.all([
        dispatch(snactions.addSnackbarItem(`Failed to save preferences, please try again later.`)),
        // refetch prospects to reset incorrect local state
        dispatch(prospectActions.fetchProspects()),
      ])
    } finally {
      handleClose()
    }
  }

  const handleSubmitSurvey = () => {
    if (followSurveyRef.current) {
      const fn = followSurveyRef.current
      followSurveyRef.current = null
      fn()
    }
    setShowSurvey(false)
    setOnFollow(false)
  }

  if (!studentShareInterestEnabled) {
    return <>{children}</>
  }

  return (
    <ShareInterestContext.Provider
      value={{
        open: open,
        handleOpen,
        handleClose,
        handleDismiss,
        handleAfterClose,
        selectedCollege,
        selectCollege,
        handleSave,
        isSaving,
        errors,
        updateStudentInterestInfo: updateDetails,
        studentInterestInfo: student,
        requiredFieldsIncomplete,
        preferenceTypeSet: user.StudentInterestSharingPreference?.preferenceType !== undefined,
        preferenceType,
        setPreferenceType,
        firstTime,
      }}
    >
      {children}
      <StudentShareInterestModal />
      <CollegeSwitchSurvey
        type="follow"
        referrer={referrer}
        show={showSurvey}
        prospect={{ CollegeName: selectedCollege?.Name || '', CollegeScid: selectedCollege?.Scid || 0 }}
        onClose={handleSubmitSurvey}
        onSubmit={handleSubmitSurvey}
      />
    </ShareInterestContext.Provider>
  )
}

const ShareInterestWrapper = ({ children }: { children: React.ReactNode }) => {
  const user = useSelector(selectUser)
  if (!user) {
    return <>{children}</>
  }
  return <ShareInterestProvider>{children}</ShareInterestProvider>
}

export default ShareInterestWrapper

const calculateAge = (birthDate: Date) => {
  const today = new Date()

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

const shallowEqual = (a: StudentInfo, b: StudentInfo) => {
  const filterKeys = (obj: StudentInfo): (keyof StudentInfo)[] =>
    (Object.keys(obj) as (keyof StudentInfo)[]).filter((key) => {
      const value = obj[key]
      return !(Array.isArray(value) && value.length === 0)
    })

  const aKeys = filterKeys(a)
  const bKeys = filterKeys(b)

  if (aKeys.length !== bKeys.length) return false

  return aKeys.every((key) => {
    if (!(key in b)) return false
    return JSON.stringify(a[key]) === JSON.stringify(b[key])
  })
}
